/**
 * Secret redaction shared by the server (audit_log rows, see
 * server/src/api/Audit.middleware.ts) and the client (console output, see
 * client/src/app/core/http-interceptor.service.ts, and what goes to the issue
 * tracker: GraphQL variables and breadcrumbs, see
 * client/src/app/core/errors/trackingLink.ts and issueTracker.ts). No
 * dependencies: this file is compiled into both builds.
 */

/** property names whose values must never be persisted in logs, audit rows or error-tracker breadcrumbs */
export const SECRET_KEY_PATTERN =
  /passw(or)?d|passphrase|secret|token|api[-_]?key|authorization|credential/i;

export const REDACTED = "[REDACTED]";

/**
 * Deep-copies `value`, replacing the value of every property whose name looks
 * like a secret (SECRET_KEY_PATTERN, e.g. password / newPassword / token /
 * apiKey) with '[REDACTED]'. Arrays and nested objects are walked; primitives
 * are returned unchanged. Never throws.
 */
export function redactSecrets<T>(value: T): T {
  return walk(value, 0) as T;
}

/** true when redactSecrets(value) would change something */
export function containsSecrets(value: unknown): boolean {
  return JSON.stringify(walk(value, 0)) !== JSON.stringify(value);
}

function walk(value: any, depth: number): any {
  if (value === null || typeof value !== "object") return value;
  // defensive: absurdly deep (or cyclic) input is not worth persisting
  if (depth > 32) return REDACTED;
  if (Array.isArray(value)) return value.map((item) => walk(item, depth + 1));
  const out: { [key: string]: any } = {};
  for (const key of Object.keys(value)) {
    out[key] = SECRET_KEY_PATTERN.test(key)
      ? REDACTED
      : walk(value[key], depth + 1);
  }
  return out;
}

/** JSON-style `"…password…": "value"` pair; group 1 is everything up to the value's opening quote */
const JSON_SECRET_PAIR =
  /("[^"\\]*(?:passw(?:or)?d|passphrase|secret|token|api[-_]?key|authorization|credential)[^"\\]*"\s*:\s*)"(?:[^"\\]|\\.)*"/gi;
/** the same pair inside a JSON string, i.e. with its quotes escaped: `\"password\":\"value\"` */
const ESCAPED_JSON_SECRET_PAIR =
  /(\\"[^"\\]*(?:passw(?:or)?d|passphrase|secret|token|api[-_]?key|authorization|credential)[^"\\]*\\"\s*:\s*)\\"(?:[^"\\]|\\[^"])*\\"/gi;
/** `Bearer <credential>` as sent in an Authorization header */
const BEARER_CREDENTIAL = /\bBearer\s+[A-Za-z0-9\-._~+/]+=*/g;
/** a bare JWT: three base64url segments, the first one always encoding `{"` */
const JWT = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\b/g;

/**
 * Redacts secrets inside text that already holds serialized data, for the
 * places where the object is gone by the time we see it, e.g. a console
 * breadcrumb Bugsnag stringified: JSON-style pairs whose key matches
 * SECRET_KEY_PATTERN (plain or with escaped quotes), `Bearer …` credentials
 * and bare JWTs. Returns the input unchanged when it holds none of those.
 */
export function redactSecretsInText(text: string): string {
  if (typeof text !== "string" || !text) return text;
  return text
    .replace(JSON_SECRET_PAIR, `$1"${REDACTED}"`)
    .replace(ESCAPED_JSON_SECRET_PAIR, `$1\\"${REDACTED}\\"`)
    .replace(BEARER_CREDENTIAL, `Bearer ${REDACTED}`)
    .replace(JWT, REDACTED);
}
